/**
 * Subscription Service — API calls del SUPER_ADMIN para gestionar
 * planes, suscripciones y pagos.
 */

import { get, post, patch } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  Plan,
  SubscriptionWithPlan,
  InstitutionSubscription,
  InstitutionPayment,
  AssignSubscriptionInput,
  RecordPaymentInput,
  UpdatePlanPricingInput,
  ExtendTrialInput,
  SuspendSubscriptionInput,
  PaymentListResponse,
} from '@/domain/subscription/types'

// ===============================================================
// Plans
// ===============================================================

export async function listPlans(): Promise<{ plans: Plan[] }> {
  return get<{ plans: Plan[] }>(API_ROUTES.PLANS.BASE)
}

export async function updatePlan(
  id: string,
  data: Partial<Plan>
): Promise<{ plan: Plan }> {
  return patch<{ plan: Plan }>(API_ROUTES.PLANS.BY_ID(id), data)
}

export async function updatePlanPricing(
  id: string,
  data: UpdatePlanPricingInput
): Promise<{ plan: Plan }> {
  return patch<{ plan: Plan }>(API_ROUTES.PLANS.PRICING(id), data)
}

// ===============================================================
// Subscriptions
// ===============================================================

export async function getInstitutionSubscription(
  institutionId: string
): Promise<{ subscription: SubscriptionWithPlan }> {
  return get<{ subscription: SubscriptionWithPlan }>(
    API_ROUTES.INSTITUTIONS.SUBSCRIPTION(institutionId)
  )
}

export async function assignSubscription(
  institutionId: string,
  data: AssignSubscriptionInput
): Promise<{ subscription: InstitutionSubscription }> {
  return post<{ subscription: InstitutionSubscription }>(
    API_ROUTES.INSTITUTIONS.SUBSCRIPTION(institutionId),
    data
  )
}

export async function suspendSubscription(
  subscriptionId: string,
  data: SuspendSubscriptionInput
): Promise<{ subscription: InstitutionSubscription }> {
  return post<{ subscription: InstitutionSubscription }>(
    API_ROUTES.SUBSCRIPTIONS.SUSPEND(subscriptionId),
    data
  )
}

export async function reactivateSubscription(
  subscriptionId: string
): Promise<{ subscription: InstitutionSubscription }> {
  return post<{ subscription: InstitutionSubscription }>(
    API_ROUTES.SUBSCRIPTIONS.REACTIVATE(subscriptionId),
    {}
  )
}

export async function extendTrial(
  subscriptionId: string,
  data: ExtendTrialInput
): Promise<{ subscription: InstitutionSubscription }> {
  return post<{ subscription: InstitutionSubscription }>(
    API_ROUTES.SUBSCRIPTIONS.EXTEND_TRIAL(subscriptionId),
    data
  )
}

// ===============================================================
// Payments
// ===============================================================

export async function recordPayment(
  institutionId: string,
  data: RecordPaymentInput
): Promise<{ payment: InstitutionPayment }> {
  return post<{ payment: InstitutionPayment }>(
    API_ROUTES.INSTITUTIONS.PAYMENTS(institutionId),
    data
  )
}

export async function listPayments(
  institutionId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<PaymentListResponse> {
  const params = new URLSearchParams()
  if (options.limit) params.append('limit', String(options.limit))
  if (options.offset) params.append('offset', String(options.offset))

  const query = params.toString()
  const url = query
    ? `${API_ROUTES.INSTITUTIONS.PAYMENTS(institutionId)}?${query}`
    : API_ROUTES.INSTITUTIONS.PAYMENTS(institutionId)

  return get<PaymentListResponse>(url)
}
